import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { HiArrowRight, HiArrowUpRight } from "react-icons/hi2";
import { usePublicPage } from "../../../../hooks/usePublicPage";
import { usePublicBlogPosts } from "../../../../hooks/usePublicBlogPosts";
import { getApiErrorMessage } from "../../../../utils/apiResponse";
import { mapBlogListItem } from "../../../../utils/blog";
import { useLanguage } from "../../../../context/LanguageContext";
import { usePrefetchPublicRoute } from "../../../../hooks/usePrefetchPublicRoute";
import "./NewsRoom.css";

const requireField = (fields, key) => {
  const value = fields?.[key];
  if (value == null || String(value).trim() === "") {
    throw new Error(`Newsroom field "${key}" is missing from the API response.`);
  }
  return value;
};

const NewsRoom = () => {
  const { language, meta } = useLanguage();
  const prefetchRoute = usePrefetchPublicRoute();
  const pageQuery = usePublicPage("home");
  const postsQuery = usePublicBlogPosts({ home: true, take: 3 });

  const { header, headerError } = useMemo(() => {
    if (!pageQuery.data) {
      return { header: null, headerError: null };
    }

    try {
      const section = pageQuery.data.sections?.find((s) => s.key === "newsroom");
      if (!section) {
        throw new Error("Newsroom section was not found on the home page.");
      }

      const f = section.fields || {};
      return {
        header: {
          badge: requireField(f, "badge"),
          title: requireField(f, "title"),
          viewAllText: requireField(f, "viewAllText"),
          viewAllUrl: requireField(f, "viewAllUrl"),
        },
        headerError: null,
      };
    } catch (err) {
      return {
        header: null,
        headerError: err instanceof Error ? err.message : String(err),
      };
    }
  }, [pageQuery.data]);

  const items = useMemo(
    () => (postsQuery.data || []).map((p) => mapBlogListItem(p, language)),
    [postsQuery.data, language]
  );

  const error =
    (pageQuery.error != null ? getApiErrorMessage(pageQuery.error) : null) ||
    headerError ||
    (postsQuery.error != null ? getApiErrorMessage(postsQuery.error) : null);

  const loading =
    (pageQuery.isPending && !header) || (postsQuery.isPending && !postsQuery.data);

  if (loading) {
    return (
      <section className="newsroom" dir={meta.dir}>
        <div className="newsroom__container">
          <p style={{ color: "#64748b", fontSize: 14 }}>Loading news…</p>
        </div>
      </section>
    );
  }

  if (error || !header) {
    return (
      <section className="newsroom" dir={meta.dir}>
        <div className="newsroom__container">
          <p style={{ color: "#b91c1c", fontSize: 14 }}>
            {error || "Newsroom unavailable."}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="newsroom" dir={meta.dir}>
      <div className="newsroom__container">
        <span className="newsroom__badge">{header.badge}</span>

        <div className="newsroom__header">
          <h2 className="newsroom__title">{header.title}</h2>
          <Link to={header.viewAllUrl} className="newsroom__view-all">
            <span>{header.viewAllText}</span>
            <HiArrowRight />
          </Link>
        </div>

        <div className="newsroom__grid">
          {items.map((item) => (
            <Link
              key={item.id}
              to={`/news/${item.slug}`}
              className="newsroom__card"
              onMouseEnter={() => prefetchRoute(`/news/${item.slug}`)}
              onFocus={() => prefetchRoute(`/news/${item.slug}`)}
            >
              <img
                src={item.image}
                alt={item.imageAlt}
                className="newsroom__card-image"
              />
              <time className="newsroom__card-date">{item.date}</time>
              <div className="newsroom__card-title-row">
                <h3 className="newsroom__card-title">{item.title}</h3>
                <HiArrowUpRight className="newsroom__card-arrow" aria-hidden="true" />
              </div>
              <p className="newsroom__card-description">{item.description}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default NewsRoom;
